import React, { useState, memo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Stack,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import UpgradeSymbols from './UpgradeSymbols';
import { extractUpgradeSymbols } from '../../../util/upgradeSymbolsHelper';
import BookingDetailsModal from '../BookingDetailsModal/BookingDetailsModal';
import dayjs from 'dayjs';
import useNow from '../../../hooks/useNow';
import {
  isInactiveBookingStatus,
  isWaitingForPaymentActionable,
  NO_SHOW_GRACE_MINUTES_AFTER_START,
} from '../../../util/dateUtils';

const TextWithLineBreaks = ({ text }) => {
  return (
    <div>
      {text.split(/\r\n|\n/).map((line, index) => (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      ))}
    </div>
  );
};

// Same pending-attention logic as in InternalBookingCardBase and VerticalBookingCardBase
const getShowAttentionStripes = (booking, isWaitingForPayment, entertainmentType) => {
  if (isInactiveBookingStatus(booking?.status)) return false;
  if (isWaitingForPayment) return false; // handled separately with orange stripes

  const presentSince = booking?.presentSince;
  const finishedAt = booking?.finishedAt;
  const hasPayableAmount = parseFloat(booking?.totalAmount || 0) > 0;
  const isEffectivelyUnpaid = !booking?.fullyPaid && hasPayableAmount;

  const now = dayjs();
  const startRaw = booking?.startTime || booking?.eventStart;
  const start = startRaw ? dayjs(startRaw) : null;
  const endRaw = booking?.endTime || booking?.eventEnd;
  const end = endRaw ? dayjs(endRaw) : null;

  if (!presentSince && start?.isValid()) {
    const eligibleAt = start.add(NO_SHOW_GRACE_MINUTES_AFTER_START, 'minute');
    if (now.isAfter(eligibleAt) || now.isSame(eligibleAt)) return true;
  }
  if (presentSince && !finishedAt && isEffectivelyUnpaid) return true;
  if (!finishedAt && end?.isValid()) {
    if (now.isAfter(end.add(15, 'minute')) || now.isSame(end.add(30, 'minute'))) return true;
  }
  // LaserZone only: present but briefing not done
  if (entertainmentType === 'LaserZone' && presentSince && !finishedAt && !booking?.ltSpecific?.briefedAt) {
    return true;
  }
  return false;
};

const getIsWaitingForPaymentActionable = (booking, nowTs) => {
  return isWaitingForPaymentActionable(booking, nowTs);
};

// A booking uses ordered resources (multiple sequential segments) when it has a
// non-empty orderedResourceIds array. Those are handled by dedicated grid views
// and must NOT show a single resource in the list column.
const hasOrderedResources = booking =>
  Array.isArray(booking?.orderedResourceIds) && booking.orderedResourceIds.length > 0;

// Resource name to display in the list for resource-based (single resource)
// bookings. Returns an empty string for ordered-resource or non-resource bookings.
const getSingleResourceName = booking => {
  if (hasOrderedResources(booking)) return '';
  return booking?.resourceId?.name || '';
};

function BookingTable({ bookings, updateBooking, entertainmentType }) {
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [modalBookingId, setModalBookingId] = useState(null);
  const nowTs = useNow();

  const handleOpenDetailsModal = async bookingId => {
    setModalBookingId(bookingId);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setModalBookingId(null);
  };

  const colorCalc = teamId => {
    if (teamId !== 9) {
      if (teamId == 0) {
        return 'orange';
      } else if (teamId == 1) {
        return 'green';
      } else if (teamId == 2) {
        return 'blue';
      } else if (teamId == 3) {
        return 'deeppink';
      }
    } else {
      return 'white';
    }
  };

  const openRegiondo = bookingKey => {
    window.open(
      'https://www.regiondo.de/resellers/account/bookingsview/key/' + bookingKey,
      '_blank'
    );
  };

  const renderStatusIcons = booking => {
    const presentSince = booking?.presentSince;
    const finishedAt = booking?.finishedAt;
    const hasPayableAmount = parseFloat(booking?.totalAmount || 0) > 0;
    const isEffectivelyUnpaid = !booking?.fullyPaid && hasPayableAmount;
    const isAwaitingConfirmation = booking?.status === 'awaitingConfirmation';
    const isWaitingForPaymentStatus = booking?.status === 'waitingForPayment';
    const isWaitingForPaymentActionableFlag = getIsWaitingForPaymentActionable(booking, nowTs);
    const isLaser = entertainmentType === 'LaserZone';
    const needsBriefing =
      isLaser && presentSince && !finishedAt && !isEffectivelyUnpaid && !booking?.ltSpecific?.briefedAt;

    const icons = [];

    if (isAwaitingConfirmation) {
      icons.push(
        <Tooltip key="awaiting" title="Bestätigung ausstehend">
          <PendingIcon sx={{ color: 'warning.main', width: 18, height: 18 }} />
        </Tooltip>
      );
    }
    if (presentSince && !finishedAt) {
      icons.push(
        <Tooltip key="checked-in" title="Eingecheckt">
          <CheckCircleIcon sx={{ color: 'success.main', width: 18, height: 18 }} />
        </Tooltip>
      );
    }
    if (finishedAt) {
      icons.push(
        <Tooltip key="checked-out" title="Ausgecheckt">
          <CheckCircleIcon sx={{ color: 'error.main', width: 18, height: 18 }} />
        </Tooltip>
      );
    }
    if (isEffectivelyUnpaid || isWaitingForPaymentStatus) {
      icons.push(
        <Tooltip key="unpaid" title="Unbezahlt">
          <PaidIcon sx={{ color: 'error.main', width: 18, height: 18 }} />
        </Tooltip>
      );
    }
    if (needsBriefing) {
      icons.push(
        <Tooltip key="briefing" title="Briefing ausstehend">
          <RecordVoiceOverIcon sx={{ color: 'info.main', width: 18, height: 18 }} />
        </Tooltip>
      );
    }

    if (icons.length === 0) return null;
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {icons}
      </Stack>
    );
  };

  // Only show the resource column when at least one resource-based booking
  // (single resource, not ordered resources) is present in the current list.
  const showResourceColumn = bookings.some(booking => getSingleResourceName(booking));

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 56, minWidth: 56 }}></TableCell>
              <TableCell>Uhrzeit</TableCell>
              <TableCell align='left'>Name</TableCell>
              <TableCell align='left'>Produkt</TableCell>
              {showResourceColumn && <TableCell align='left'>Ressource</TableCell>}
              <TableCell align='left'>Anzahl</TableCell>
              <TableCell align='left'>Upgrades</TableCell>
              <TableCell align='left'>Kommentar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.map(booking => {
              const start = new Date(
                booking.bookingType === 'RegiondoBooking' ? booking.eventStart : booking.startTime
              );
              const duration = booking.eventDurationMS / 1000 / 60 / 60;
              const isWaitingForPaymentHighlight = getIsWaitingForPaymentActionable(booking, nowTs);
              const showAttentionStripes = getShowAttentionStripes(booking, isWaitingForPaymentHighlight, entertainmentType);

              const rowSx = {
                '&:last-child td, &:last-child th': { border: 0 },
                ...(isWaitingForPaymentHighlight && {
                  background: `repeating-linear-gradient(
                    -45deg,
                    transparent,
                    transparent 6px,
                    rgba(237, 108, 2, 0.35) 6px,
                    rgba(237, 108, 2, 0.35) 12px
                  )`,
                }),
                ...(showAttentionStripes && !isWaitingForPaymentHighlight && {
                  background: `repeating-linear-gradient(
                    -45deg,
                    transparent,
                    transparent 6px,
                    rgba(33, 150, 243, 0.25) 6px,
                    rgba(33, 150, 243, 0.25) 12px
                  )`,
                }),
              };

              return (
                <TableRow
                  key={booking._id}
                  sx={rowSx}
                >
                  <TableCell padding="checkbox" sx={{ width: 56, minWidth: 56, verticalAlign: 'middle' }}>
                    {renderStatusIcons(booking)}
                  </TableCell>
                  <TableCell component='th' scope='row'>
                    {start.toLocaleTimeString().substring(0, 5)}
                  </TableCell>
                  <TableCell
                    align='left'
                    onClick={() => {
                      if (booking.bookingType === 'RegiondoBooking') {
                        openRegiondo(booking.regiondoMeta?.booking_key);
                      } else if (booking.bookingType === 'InternalBooking') {
                        handleOpenDetailsModal(booking._id);
                      }
                    }}
                    sx={{
                      color: colorCalc(
                        booking.ltSpecific?.playerGroupID ??
                          booking.pbSpecific?.playerGroupID ??
                          undefined
                      ),
                      cursor: 'pointer',
                    }}
                  >
                    {booking.firstName} {booking.lastName}
                  </TableCell>
                  <TableCell align='left'>
                    {booking.bookingType === 'RegiondoBooking'
                      ? booking.regiondoMeta?.product_name
                      : `${parseFloat(duration.toFixed(1))} Stunden`}
                    <br />
                    <Typography variant='caption'>
                      {booking.bookingType === 'RegiondoBooking' &&
                        (booking.entertainment == 'BashZone'
                          ? `${booking.regiondoMeta?.variation_name}`
                          : `${booking.regiondoMeta?.option_name}`)}
                    </Typography>
                  </TableCell>
                  {showResourceColumn && (
                    <TableCell align='left'>{getSingleResourceName(booking)}</TableCell>
                  )}
                  <TableCell align='left'>{booking.participantCount}</TableCell>
                  <TableCell align='left'>
                    <UpgradeSymbols
                      upgrades={
                        booking.bookingType === 'RegiondoBooking'
                          ? booking.specialUpgrades
                          : extractUpgradeSymbols(booking)
                      }
                      manualUpgrades={booking.manualUpgrades}
                      voucherSoldDate={booking.voucherSoldDate}
                    />
                  </TableCell>
                  <TableCell align='left'>
                    {booking.comment ? <TextWithLineBreaks text={booking.comment} /> : ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <BookingDetailsModal
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        bookingId={modalBookingId}
        onUpdate={updateBooking}
      />
    </Box>
  );
}

export default memo(BookingTable);
